import type { InvitedModes } from "api/models/draftRequest.model";
import type { AvailableFreightMode } from "api/response/location.response";

export function getCustomFreightIndex(value: string, invitedModes: InvitedModes | undefined) {
  const [, key] = value.split("_");
  const existingCustomItems = invitedModes?.custom ?? [];
  const existingValueIndex = existingCustomItems.findIndex((item) => +item.key === +key);
  return {
    key,
    index: existingValueIndex === -1 ? existingCustomItems.length : existingValueIndex,
  };
}

export function isRoadFreightOptionValue(value: string, availableModes: AvailableFreightMode[]): boolean {
  const roadMode = availableModes.find((m) => m.value === "road_freight");
  return Boolean(roadMode?.options?.some((opt) => opt.value === value));
}

export function isInvitedModeSelected(
  invitedModes: InvitedModes | undefined,
  value: string,
  availableModes: AvailableFreightMode[] = [],
): boolean {
  if (!invitedModes) return false;

  if (value.includes("custom_")) {
    const { key } = getCustomFreightIndex(value, invitedModes);
    return (invitedModes.custom ?? []).some((item) => String(item.key) === key && item.value);
  }

  if (isRoadFreightOptionValue(value, availableModes)) {
    return invitedModes.road_freight === true && invitedModes.road_freight_option === value;
  }

  return invitedModes[value] === true;
}

export function getInvitedModeKeysForApi(invitedModes: InvitedModes | undefined): string[] {
  if (!invitedModes) return [];

  const modes: string[] = [];
  Object.keys(invitedModes).forEach((key) => {
    if (typeof invitedModes[key] === "boolean" && invitedModes[key] && !key.includes("custom")) {
      modes.push(key);
    }
    if (key === "custom" && Array.isArray(invitedModes.custom)) {
      invitedModes.custom.forEach((item) => {
        if (item.value) modes.push(`custom_${item.key}`);
      });
    }
  });
  return modes;
}

export function getSelectedInvitedModeValues(invitedModes: InvitedModes | undefined): string[] {
  if (!invitedModes) return [];

  const modes: string[] = [];
  Object.entries(invitedModes).forEach(([key, value]) => {
    if (key === "custom" && Array.isArray(value)) {
      value.forEach((item) => {
        if (item.value) modes.push(`custom_${item.key}`);
      });
    } else if (key === "road_freight_option" && typeof value === "string" && value) {
      modes.push(value);
    } else if (value === true) {
      modes.push(key);
    }
  });
  return modes;
}

export function buildInvitedModesAfterToggle(
  invitedModes: InvitedModes | undefined,
  value: string,
  availableModes: AvailableFreightMode[],
): InvitedModes {
  const next: InvitedModes = { ...(invitedModes ?? {}) };
  const isSelected = isInvitedModeSelected(next, value, availableModes);

  if (value.includes("custom_")) {
    const { index, key } = getCustomFreightIndex(value, next);
    const custom = [...(next.custom ?? [])];
    custom[index] = { key: +key, value: !isSelected };
    next.custom = custom;
    return next;
  }

  if (isRoadFreightOptionValue(value, availableModes)) {
    if (isSelected) {
      next.road_freight = false;
      next.road_freight_option = "";
    } else {
      next.road_freight = true;
      next.road_freight_option = value;
    }
    return next;
  }

  if (isSelected) {
    next[value] = false;
    if (value === "road_freight") {
      next.road_freight_option = "";
    }
  } else {
    next[value] = true;
  }

  return next;
}

export function buildInvitedModesAfterSelectAllType(
  invitedModes: InvitedModes | undefined,
  typeValues: string[],
  allSelected: boolean,
  availableModes: AvailableFreightMode[],
): InvitedModes {
  let next = { ...(invitedModes ?? {}) };

  if (allSelected) {
    typeValues.forEach((value) => {
      if (isInvitedModeSelected(next, value, availableModes)) {
        next = buildInvitedModesAfterToggle(next, value, availableModes);
      }
    });
  } else {
    typeValues.forEach((value) => {
      if (!isInvitedModeSelected(next, value, availableModes)) {
        next = buildInvitedModesAfterToggle(next, value, availableModes);
      }
    });
  }

  return next;
}

export function buildDefaultInvitedModes(availableModes: AvailableFreightMode[]): InvitedModes {
  const next: InvitedModes = {};

  availableModes.forEach((mode) => {
    if (mode.value.includes("custom")) {
      const [, key] = mode.value.split("_");
      const custom = [...(next.custom ?? [])];
      custom.push({ key: +key, value: true });
      next.custom = custom;
    } else {
      next[mode.value] = true;
    }

    if (mode.value === "road_freight") {
      const preselectedOption = mode.options?.find((option) => option.pre_select);
      if (preselectedOption) {
        next.road_freight_option = preselectedOption.value;
      }
    }
  });

  return next;
}

export function hasAirOrOceanInvitedMode(
  invitedModes: InvitedModes | undefined,
  availableModes: AvailableFreightMode[],
): boolean {
  const selected = getSelectedInvitedModeValues(invitedModes);
  if (!selected.length) return false;

  return selected.some((modeValue) => {
    const mode = availableModes.find(
      (m) => m.value === modeValue || m.options?.some((opt) => opt.value === modeValue),
    );
    return mode && ["air", "ocean"].includes(mode.type);
  });
}
